import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { Document, DocumentStats } from '../types';
import { ERROR_MESSAGES } from '../utils/constants';
import { apiRequest } from '../config/api';
import { handleSafariError } from '../utils/safariUtils';

interface UseDocumentsReturn {
  documents: Document[];
  stats: DocumentStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDocuments = (): UseDocumentsReturn => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [stats, setStats] = useState<DocumentStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocumentsFromSupabase = useCallback(async () => {
    try {
      console.log('Falling back to Supabase for document fetch...');
      const { data, error: supabaseError } = await supabase.from('documents').select('*');
      
      if (supabaseError) {
        console.error('Supabase Error:', supabaseError);
        setError(ERROR_MESSAGES.FETCH_DOCUMENTS_FAILED);
        return;
      }
      
      if (data) {
        // Transform Supabase data to match RAG API format
        const transformedData: Document[] = data.map(doc => ({
          id: doc.id,
          title: doc.title,
          filename: doc.filename,
          sector_id: doc.sector_id,
          analyst_id: undefined, // V2: analysts in junction table
          company_id: doc.company_id,
          sector_name: doc.sector_name || 'Unknown Sector',
          analyst_name: doc.analyst_name || 'Unknown Analyst',
          company_name: doc.company_name || doc.primary_ticker || 'Unknown Company',
          company_ticker: doc.primary_ticker || 'N/A',
          chunk_count: doc.chunk_count || 0,
          created_at: doc.created_at,
          timestamp: doc.business_time || doc.created_at, // V2: use business_time
          text_content: doc.text_content,
          storage_url: doc.storage_url
        }));
        
        setDocuments(transformedData);
        console.log('Successfully fetched documents from Supabase');
      }
    } catch (err) {
      console.error('Supabase Fallback Error:', err);
      setError(ERROR_MESSAGES.FETCH_DOCUMENTS_FAILED);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data: Document[] = await apiRequest('/rag/documents');
      setDocuments(data);
    } catch (err) {
      console.error('RAG API Fetch Error:', err);
      
      // Fallback to Supabase for Safari or network errors
      if (handleSafariError(err)) {
        console.log('Using Supabase fallback due to Safari or network error');
        await fetchDocumentsFromSupabase();
      } else {
        setError(ERROR_MESSAGES.FETCH_DOCUMENTS_FAILED);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchDocumentsFromSupabase]);

  const fetchStats = useCallback(async () => {
    try {
      const data: DocumentStats = await apiRequest('/rag/health');
      setStats(data);
    } catch (err) {
      // Stats are not critical, so we don't show error
      console.warn('Failed to fetch stats:', err);
    }
  }, []);

  const refetch = useCallback(async () => {
    await Promise.all([fetchDocuments(), fetchStats()]);
  }, [fetchDocuments, fetchStats]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    documents,
    stats,
    loading,
    error,
    refetch
  };
}; 